import { _isEmpty, _isEmptyString, _isNil, _isNotEmpty, _notNil, safelyDecodeURIComponent } from '@/littledash';
import { ID } from '@/model/Common.model';
import type { MetadataFieldTypes } from '@/model/Metadata.model';
import validate from 'validate.js';
import type { ExtendedMetadataField } from '../SpeciesStrains/SpeciesComponent.model';
import { ExtendedMetadataItem } from './Repeater/IndividualSaveRepeater.utils';

interface MetadataGlossaryItem {
  id: number;
  api_id?: string;
  type: string;
  header?: string;
  title: string;
  field_type: MetadataFieldTypes;
  active?: boolean;
  in_use?: boolean;
  options?: Array<string>;
  read_only: boolean;
  slug: string;
  created_at?: string;
  required?: boolean;
}

interface ValidationSchema {
  title: {
    checkDuplicate: object;
    presence: { allowEmpty: boolean; message: string };
  };
  options?: {
    metaOptions: string;
  };
}

export const encodeMetadata = (row: MetadataGlossaryItem, index: number): ExtendedMetadataItem => {
  let { options } = row;
  const { field_type } = row;

  if (field_type === 'multi_select' || field_type === 'select') {
    options = options?.map((option: string) => safelyDecodeURIComponent(option));
  }

  return {
    index,
    id: row.id,
    api_id: row.api_id,
    title: row.title,
    type: row.type,
    field_type: row.field_type,
    options,
    active: row.active,
    in_use: row.in_use,
    slug: row.slug,
    created_at: row.created_at,
    required: row.required,
    expanded: false,
    new: false,
    value: '',
    glossary_id: row.id,
    read_only: false,
  };
};

export const createInitialDataLookup = (initialData: Array<ExtendedMetadataItem>): Record<ID, ExtendedMetadataItem> => {
  return initialData.reduce<Record<ID, ExtendedMetadataItem>>((acc, val) => {
    if (_notNil(val.api_id)) {
      acc[val.api_id] = val;
    }

    if (_notNil(val.id)) {
      acc[String(val.id)] = val;
    }
    return acc;
  }, {});
};

export const sortMetadataByActive = (a: ExtendedMetadataItem, b: ExtendedMetadataItem): number => {
  return (b.active === true ? 1 : 0) - (a.active === true ? 1 : 0);
};

export const filterMetadataTypesByFeature = (disease_induction: boolean) => {
  return disease_induction ? metaDataTypes.slice() : metaDataTypes.filter(({ key }) => key !== 'disease_meta');
};

export const validateMetaData = (rowData: ExtendedMetadataField, formData: Array<ExtendedMetadataField>) => {
  validate.validators.checkDuplicate = function (value: string) {
    if (_isNil(value) || _isEmptyString(value)) {
      return null;
    }

    const duplicates = formData.filter(
      ({ active, title, type }) =>
        type === rowData.type && _notNil(title) && title.toLowerCase() === value.toLowerCase()
    );

    if (duplicates.length >= 2) {
      return 'Category must be unique.';
    } else {
      return null;
    }
  };

  const schema: ValidationSchema = {
    title: {
      checkDuplicate: {},
      presence: { allowEmpty: false, message: "Category can't be blank" },
    },
  };

  const selectFields = ['select', 'multi_select'];

  if (_notNil(rowData.field_type) && selectFields.includes(rowData.field_type)) {
    validate.validators.metaOptions = function (value: Array<string>) {
      if (_isNotEmpty(value)) {
        const checkValues = value.map((v) => (v === '' ? "Option can't be blank" : false));
        if (checkValues.every((o) => o === false)) {
          return null;
        } else {
          return [checkValues];
        }
      } else {
        return 'Terms must have values to select from';
      }
    };

    schema.options = {
      metaOptions: 'test',
    };
  }

  return validate(rowData, schema, { fullMessages: false });
};

/**
 * Validates metadata items using their native ExtendedMetadataItem type
 * @param rowData - The metadata item to validate
 * @param formData - All metadata items for duplicate checking
 * @returns Validation errors or undefined if valid
 */
export const validateExtendedMetadataItem = (rowData: ExtendedMetadataItem, formData: Array<ExtendedMetadataItem>) => {
  validate.validators.checkDuplicate = function (value: string) {
    if (_isNil(value) || _isEmptyString(value)) {
      return null;
    }

    const duplicates = formData.filter(
      ({ active, title, type }) =>
        type === rowData.type && _notNil(title) && title.toLowerCase() === value.toLowerCase()
    );

    if (duplicates.length >= 2) {
      return 'Category must be unique.';
    } else {
      return null;
    }
  };

  const schema: ValidationSchema = {
    title: {
      checkDuplicate: {},
      presence: { allowEmpty: false, message: "Category can't be blank" },
    },
  };

  const selectFields = ['select', 'multi_select'];

  if (_notNil(rowData.field_type) && selectFields.includes(rowData.field_type)) {
    validate.validators.metaOptions = function (value: Array<string>) {
      if (_isNotEmpty(value)) {
        const checkValues = value.map((v) => (v === '' ? "Option can't be blank" : false));
        if (checkValues.every((o) => o === false)) {
          return null;
        } else {
          return [checkValues];
        }
      } else {
        return 'Terms must have values to select from';
      }
    };

    schema.options = {
      metaOptions: 'test',
    };
  }

  return validate(rowData, schema, { fullMessages: false });
};

export const metaDataTypes = [
  {
    key: 'study_meta',
    title: 'Study',
  },
  {
    key: 'group_meta',
    title: 'Groups',
  },
  {
    key: 'subject_meta',
    title: 'Animals',
  },
  {
    key: 'collection_meta',
    title: 'Cages',
  },
  {
    key: 'sample_meta',
    title: 'Samples',
  },
  {
    key: 'attachment_meta',
    title: 'Attachments',
  },
  {
    key: 'treatment_meta',
    title: 'Treatments',
  },
  {
    key: 'disease_meta',
    title: 'Disease',
  },
];
